/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Create a blank activity through distinct Desktop dialog and Mobile full-page flows.
 * @state-owner local composer draft; TanStack Query owns API entities; dataset store owns the rendered projection
 * @allowed-imports activities public API/store; app active context; shared overlay, loading, dataset and navigation
 * @forbidden routes; context-id parsing; client-side authorization decisions
 * @public-api Agent A workspace feature-composition slot
 * @side-effects opens UI surfaces and submits POST /api/v1/activities
 * @tests frontend/apps/web/e2e/workspace.spec.ts, frontend/apps/web/e2e/act-f13.spec.ts and
 *   app/feature-composition/WorkspaceSectionActions.test.tsx
 */

import { type FormEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WorkspaceActionSurface } from '../../../app/feature-composition/types';
import { useActiveWorkspaceContext } from '../../../app/providers/ActiveRoleProvider';
import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { VispoInlineLoading } from '../../../shared/components/WorkspaceState';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator, useWorkspacePick } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useCreateActivity } from '../api/activities.mutations';
import { useSelectedActivityClass } from '../model/use-activity-filter';

function commandKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `activity-${Date.now()}-${Math.random()}`;
}

function createErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'status' in error) {
    if (error.status === 403) return '你目前沒有在這個工作區建立活動的權限。';
    if (error.status === 409) return '同一份活動仍在建立中，請稍後重試。';
  }
  return error instanceof Error && error.message ? error.message : '目前無法建立活動，請重試。';
}

export function CreateActivityButton({ surface }: { surface: WorkspaceActionSurface }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const classIndex = useSelectedActivityClass();
  const [mobileOpen, setMobileOpen] = useState(false);

  const openBlankDesktop = () => {
    openModal(<DesktopBlankActivityDialog initialClassIndex={classIndex} />);
  };

  if (surface !== 'desktop') {
    return (
      <>
        <button
          aria-label={t('新增活動')}
          className={surface === 'mobile-list' ? 'mobile-top-bar__icon' : 'bt pri addbt'}
          type="button"
          onClick={() => setMobileOpen(true)}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {surface === 'mobile-detail' ? t('新增') : null}
        </button>
        {mobileOpen
          ? createPortal(
              <MobileBlankActivityPage onClose={() => setMobileOpen(false)} />,
              document.body,
            )
          : null}
      </>
    );
  }

  return (
    <>
      {classIndex != null ? (
        <button
          className="bt"
          id="tgoto"
          style={{ marginRight: 8 }}
          type="button"
          onClick={() => navigate('room', `c${classIndex}`)}
        >
          {t('前往班級')}
        </button>
      ) : null}
      <button
        className="bt pri addbt"
        id="tadd"
        title={t('建立空白活動')}
        type="button"
        onClick={openBlankDesktop}
      >
        {t('建立空白活動')}
        <svg aria-hidden="true" viewBox="0 0 24 24">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
    </>
  );
}

function DesktopBlankActivityDialog({ initialClassIndex }: { initialClassIndex: number | null }) {
  const { closeModal } = useOverlay();
  return (
    <ModalDocument
      className="activity-create-dialog"
      maxWidth={520}
      subtitle="先建立空白題紙，再進入編輯器加入題目。"
      title="建立空白活動"
    >
      <BlankActivityForm
        initialClassIndex={initialClassIndex}
        submitLabel="建立並開啟"
        onCreated={closeModal}
      />
    </ModalDocument>
  );
}

function MobileBlankActivityPage({ onClose }: { onClose(): void }) {
  return (
    <section aria-label="建立空白活動" className="activity-create-mobile mobile-page">
      <header className="mobile-page__header">
        <button aria-label="返回活動" className="mobile-page__back" type="button" onClick={onClose}>
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <h1>新增活動</h1>
        <span aria-hidden="true" className="mobile-workspace-header__detail-spacer" />
      </header>
      <div className="activity-create-mobile__body mobile-page__body">
        <div className="activity-create-mobile__intro">
          <span aria-hidden="true">＋</span>
          <h2>建立空白題紙</h2>
          <p>命名後直接進入編輯器，題目可以稍後再加入。</p>
        </div>
        <BlankActivityForm initialClassIndex={null} submitLabel="建立並開啟" onCreated={onClose} />
      </div>
    </section>
  );
}

function BlankActivityForm({
  initialClassIndex,
  submitLabel,
  onCreated,
}: {
  initialClassIndex: number | null;
  submitLabel: string;
  onCreated(): void;
}) {
  const { contextId, organizationId } = useActiveWorkspaceContext();
  const classes = useDatasetStore((state) => state.classes);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const pick = useWorkspacePick();
  const createActivity = useCreateActivity();
  const [title, setTitle] = useState('');
  const [classroomId, setClassroomId] = useState(
    initialClassIndex == null ? '' : (classes[initialClassIndex]?.id ?? ''),
  );
  const titleInput = useRef<HTMLInputElement>(null);
  const command = useRef<{ key: string; signature: string } | null>(null);

  useEffect(() => {
    titleInput.current?.focus();
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedTitle = title.trim();
    if (!organizationId || !normalizedTitle) return;
    const signature = JSON.stringify({ classroomId: classroomId || null, normalizedTitle });
    if (command.current?.signature !== signature) {
      command.current = { key: commandKey(), signature };
    }
    try {
      const activity = await createActivity.mutateAsync({
        organizationId,
        title: normalizedTitle,
        classroomId: classroomId || null,
        curriculumUnitId: null,
        idempotencyKey: command.current.key,
        contextId,
      });
      const classIndex = classes.findIndex((classroom) => classroom.id === classroomId);
      const dataset = useDatasetStore.getState();
      if (!dataset.activities.some((candidate) => candidate.id === activity.id)) {
        dataset.prependActivities([
          {
            id: activity.id,
            name: activity.title,
            questionCount: 0,
            status: 'review',
            tags: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            needsReview: 0,
            classIndex: classIndex >= 0 ? classIndex : null,
            updatedLabel: '剛剛',
            reviewed: [],
            usedByClassIndexes: classIndex >= 0 ? [classIndex] : [],
          },
        ]);
      }
      notify(`已建立《${activity.title}》`);
      onCreated();
      pick(activity.id);
    } catch {
      // TanStack Query retains the error and the controlled fields preserve the user's draft.
    }
  };

  return (
    <form className="activity-create-form" onSubmit={(event) => void submit(event)}>
      <label>
        <span>活動名稱</span>
        <input
          maxLength={240}
          placeholder="例如：八年級代數小測"
          ref={titleInput}
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <label>
        <span>班級（選填）</span>
        <select value={classroomId} onChange={(event) => setClassroomId(event.target.value)}>
          <option value="">暫不指定班級</option>
          {classes.map((classroom) => (
            <option key={classroom.id} value={classroom.id}>
              {classroom.name}
            </option>
          ))}
        </select>
      </label>
      {organizationId ? null : (
        <p className="activity-create-form__error" role="alert">
          工作區仍在載入，請稍後再試。
        </p>
      )}
      {createActivity.isError ? (
        <p className="activity-create-form__error" role="alert">
          {createErrorMessage(createActivity.error)}
        </p>
      ) : null}
      <button
        className="bt pri activity-create-form__submit"
        disabled={!organizationId || !title.trim() || createActivity.isPending}
        type="submit"
      >
        {createActivity.isPending ? <VispoInlineLoading label="正在建立活動…" /> : submitLabel}
      </button>
    </form>
  );
}
